import * as Sentry from '@sentry/vue'
import {
  isDesktopOfficeHost,
  isPowerPointApiSupported,
  readSelectedSlideId,
  saveDocumentSetting,
} from '@/services/office/helpers'
import { createDebug } from '@/utils/debug'

// Toggle in the browser console with: localStorage.debug = 'aha:ppt:multiple-select'
const log = createDebug('ppt:multiple-select')

// AHA-46329: the AhaSlides slide a PowerPoint slide is bound to lives in the PowerPoint
// slide's own TAGS, not in Office.context.document.settings. Settings cannot bind a
// CLONED add-in frame: on the web they are shared across frames and get() can return
// another frame's value at random (office-js #2765), and on desktop a cloned slide
// simply inherits the donor's settings snapshot. Either way every clone would come up
// rendering the same AhaSlides slide. Slide tags are per-slide and writable from the
// inserting frame, so they are the only store that can distinguish the clones.
// Tag keys are case-insensitive (PowerPoint stores them uppercased).
export const SLIDE_TAG_PRESENTATION_KEY = 'AHASLIDESPRESENTATIONID'
export const SLIDE_TAG_SLIDE_KEY = 'AHASLIDESLIDEID'

// Ids that come back from a PowerPoint slide tag or a Vue route param are STRINGS. The ids
// the rest of the app holds are numbers, and PresentationDetailUtils gates the PowerPoint
// binding on a STRICT comparison:
//   store.state.powerPointPresentationId === res.body.id   // res.body.id is a number
// A string binding silently loses that comparison, so the editor discards the frame's
// resolved slide and falls back to res.body.activeSlide — the server-wide active slide,
// which is the same in every frame. Every cloned frame then renders the same AhaSlides
// slide, defeating the entire point of the per-slide tags. Anything that flows a tag value
// or route param into the store or document.settings must pass through here first.
//
// Non-numeric input (including null/undefined, which callers use to CLEAR a binding and
// which Number() would happily turn into 0) passes through untouched.
export const toNumericId = (value) => {
  if (value === null || value === undefined || value === '') {
    return value
  }
  const numeric = Number(value)
  return Number.isFinite(numeric) ? numeric : value
}

// Slide.exportAsBase64 — the donor export the whole clone trick depends on.
const BULK_ADD_API_VERSION = '1.8'
// TagCollection, used to bind each inserted slide.
const SLIDE_TAG_API_VERSION = '1.3'

// AHA-46329: the insert budget MUST scale with the selection. The loop costs a couple of
// context.sync() round-trips plus a base64 pptx parse per inserted slide, so a flat 20s
// budget was blown by any large selection (the prominent "Select all" on a 40-slide deck),
// which timed out, rolled every inserted slide back, and made the feature unusable above
// some deck size. Budget = base + per-slide allowance; it is a runaway guard, not a
// latency target, so it is deliberately generous.
export const INSERT_TIMEOUT_BASE_MS = 15000
export const INSERT_TIMEOUT_PER_SLIDE_MS = 6000
export const insertTimeoutMs = (slideCount) => {
  return INSERT_TIMEOUT_BASE_MS + (INSERT_TIMEOUT_PER_SLIDE_MS * slideCount)
}
// Bound on how long the failure path waits for a timed-out `run` to settle before rolling
// back regardless (see waitForSettleWithGrace below).
const SETTLE_GRACE_MS = 5000

// There is no Office.js API to embed a content add-in onto a new slide, so a new slide
// carrying the add-in can only be made by cloning one that already has it. That needs
// exportAsBase64 (1.8), which is absent on iPad and on every volume-licensed perpetual
// Office (incl. 2024 LTSC). Callers must check this BEFORE offering the flow, so an
// unsupported host never reaches a dead end.
export const isBulkSlideAddSupported = () => {
  if (!window.PowerPoint || !window.PowerPoint.run) {
    return false
  }
  return isPowerPointApiSupported(BULK_ADD_API_VERSION)
}

const canReadSlideTags = () => {
  if (!window.PowerPoint || !window.PowerPoint.run) {
    return false
  }
  return isPowerPointApiSupported(SLIDE_TAG_API_VERSION)
}

// Which slide does this add-in frame live on? Office.js has no direct answer;
// getSelectedDataAsync(SlideRange) is the closest signal — on mount, the frame's own
// slide is the selected one. Resolves the 1-based index, which the PowerPoint.run
// batch turns into a canonical slide id (the ids the two APIs hand out differ in shape,
// so never compare them directly).
//
// `caller` only labels the debug log: with seven cloned frames booting at once, every one
// of them calls this and an unlabelled line cannot be attributed. The elapsed time and the
// resolved index are logged together on purpose — the index is the diagnostic that matters,
// since several frames resolving the SAME index is the selection-is-not-per-frame defect
// on paper, and the timing alone would only show the jitter that scrambles which wrong
// answer each frame gets.
const readHostSlideIndex = (caller = 'unknown') => new Promise((resolve, reject) => {
  const startedAt = performance.now()
  readSelectedSlideId((selectedId, error, slides) => {
    const elapsedMs = Math.round(performance.now() - startedAt)
    const index = slides && slides[0] && slides[0].index
    if (error || !index) {
      log(`readHostSlideIndex[${caller}] failed after ${elapsedMs}ms`, error)
      reject(new Error(error || 'Could not identify the current PowerPoint slide'))
      return
    }
    log(`readHostSlideIndex[${caller}] -> index ${index} (selected id ${selectedId}) in ${elapsedMs}ms`)
    resolve(index)
  })
})

const withTimeout = (promise, ms, message) => {
  let timer = null
  const timeout = new Promise((resolve, reject) => {
    timer = setTimeout(() => reject(new Error(message)), ms)
  })
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer))
}

// Waits for `promise` to settle, but only up to `ms` — used on the failure path to give a
// timed-out `run` a chance to finish before rollback reads its final state, WITHOUT letting
// that wait become unbounded if the batch never settles at all (e.g. a frozen/crashed host,
// exactly what the insert timeout was meant to guard against). `promise`'s own outcome is
// swallowed unconditionally — attaching `.catch` here immediately, not after the grace
// period — so it can never surface as an unhandled rejection regardless of whether the
// grace timer or the promise settles first.
const waitForSettleWithGrace = (promise, ms) => {
  const settled = promise.catch(() => {})
  let timer = null
  const grace = new Promise((resolve) => { timer = setTimeout(resolve, ms) })
  return Promise.race([settled, grace]).finally(() => clearTimeout(timer))
}

// Loads the current slide id list. Called ONCE after each insert: the newly inserted slide
// is identified by DIFFING these ids against the running list we already hold — resolving
// it by index would break the moment the user reorders slides mid-flight. The "before"
// load each iteration used to do is redundant (the previous iteration's "after" IS this
// iteration's "before"), and since every load re-fetches the WHOLE slide collection it was
// costing O(N^2) proxy traffic plus an extra sync round-trip per inserted slide.
const loadSlideIds = async (context, slides) => {
  slides.load('items/id')
  await context.sync()
  return slides.items.map((slide) => slide.id)
}

const tagSlide = (slide, presentationId, ahaSlideId) => {
  slide.tags.add(SLIDE_TAG_PRESENTATION_KEY, String(presentationId))
  slide.tags.add(SLIDE_TAG_SLIDE_KEY, String(ahaSlideId))
}

// Which id in `after` is the slide we just inserted? insertSlidesFromBase64 places it
// immediately after targetSlideId, so it is the id in that slot — NOT merely "the first new
// id in document order" (a bare diff), which would pick up a slide the USER created ABOVE
// the host mid-insert (a co-author on Office web, a Ctrl+M in the thumbnail pane). That slide
// would then be tagged with our binding, chained as the next target, and DELETED on rollback,
// destroying the user's own content. Falls back to the diff only if the positional slot is
// somehow not a new id. Returns null when nothing new is found at all.
const resolveInsertedId = (after, before, targetSlideId) => {
  const targetAt = after.indexOf(targetSlideId)
  const positional = targetAt === -1 ? null : after[targetAt + 1]
  if (positional && !before.includes(positional)) {
    return positional
  }
  return after.find((id) => !before.includes(id)) || null
}

// Removes both binding keys from a slide. delete() is a no-op when the key is absent, so
// this needs no existence check.
const clearTagsOnSlide = (slide) => {
  slide.tags.delete(SLIDE_TAG_PRESENTATION_KEY)
  slide.tags.delete(SLIDE_TAG_SLIDE_KEY)
}

// Strips any binding tags off the donor slide BEFORE it is exported for cloning.
//
// A slide's tags live in its part of the .pptx, so they ride through
// exportAsBase64 → insertSlidesFromBase64: a donor that still carries a PREVIOUS Add's
// {presentationId, slideId} produces clones that materialise holding the DONOR's binding,
// not a blank one. An add-in that activates on such a clone (Office web activates on the
// insert's sync) can read that wrong binding before our own per-clone tag overwrites it —
// a wrong slide rendered, not merely a missing tag. True on every SECOND Add from the same
// slide and after any selectSingle rebind. Runs in its own batch so the export below is
// guaranteed to see the post-strip state; the host's correct {presentationId, slideIds[0]}
// tag is re-applied in the main batch. Best-effort: a strip failure must not abort the Add.
const stripHostSlideTags = async (hostSlideIndex) => {
  try {
    await window.PowerPoint.run(async (context) => {
      clearTagsOnSlide(context.presentation.slides.getItemAt(hostSlideIndex - 1))
      await context.sync()
    })
    log('stripped stale donor tags')
  } catch (error) {
    log('strip donor tags failed', error)
    Sentry.captureException(error)
  }
}

// Undoes a failed Add: deletes the slides we inserted, and strips the tags off the host
// slide. Best-effort — a failure to roll back must not mask the original error.
//
// Deleting the clones stops a partial failure leaving orphan slides. Stripping the HOST
// tags matters because the batch may have tagged the host with {presentationId, slideIds[0]}
// before failing (or completed late, during the settle grace): left in place, that tag —
// the highest-precedence source on load — would open the presentation in presenting mode on
// the next load for an Add the user was told had FAILED. The host slide itself is the user's
// own and is never deleted; only its tags are cleared. Its settings binding is untouched by
// a failed Add, so the host frame still resolves correctly from settings on reload.
//
// Every id is resolved with getItemOrNullObject, NOT getItem: an id can legitimately be gone
// by rollback time (the user hit Ctrl+Z during the insert window), and getItem throws at
// sync for an unknown id — which, with the deletes batched, would fail the WHOLE rollback
// and orphan every other inserted slide. isNullObject lets an already-gone slide be skipped.
const rollbackInsertedSlides = async (cloneIds, hostSlideId) => {
  if (!cloneIds.length && !hostSlideId) {
    return
  }
  try {
    await window.PowerPoint.run(async (context) => {
      const clones = cloneIds.map((id) => {
        const slide = context.presentation.slides.getItemOrNullObject(id)
        slide.load('isNullObject')
        return slide
      })
      const host = hostSlideId
        ? context.presentation.slides.getItemOrNullObject(hostSlideId)
        : null
      if (host) {
        host.load('isNullObject')
      }
      await context.sync()

      clones.forEach((slide) => {
        if (!slide.isNullObject) {
          slide.delete()
        }
      })
      if (host && !host.isNullObject) {
        clearTagsOnSlide(host)
      }
      await context.sync()
    })
    log('rolled back inserted slides', cloneIds)
  } catch (error) {
    log('rollback failed', error)
    Sentry.captureException(error)
  }
}

// The document.settings key holding a frame's {presentationId, slideId} binding. Declared
// here as well as read in office/index.js because the desktop insert path below writes it
// directly — it cannot import index.js, which imports this module.
export const DATA_SLIDE_SETTING_KEY = 'dataSlidePPT'

// Resolves the host slide's id and the current slide-id list in one batch. The desktop path
// needs both before its loop starts, and unlike the web path it has no single enclosing
// batch to piggy-back the load onto.
const readHostSlideState = async (hostSlideIndex) => {
  let hostSlideId = null
  let slideIdList = []
  await window.PowerPoint.run(async (context) => {
    const { slides } = context.presentation
    const hostSlide = slides.getItemAt(hostSlideIndex - 1)
    hostSlide.load('id')
    slides.load('items/id')
    await context.sync()
    hostSlideId = hostSlide.id
    slideIdList = slides.items.map((slide) => slide.id)
  })
  return { hostSlideId, slideIdList }
}

// Writes the frame's own binding back into document.settings. The desktop loop below leaves
// settings holding the LAST clone's binding, so the host frame — whose settings these
// actually are — must be restored before anything reads them again, on the success path and
// on the failure path alike.
const restoreHostBinding = async (presentationId, hostAhaSlideId) => {
  await saveDocumentSetting(DATA_SLIDE_SETTING_KEY, {
    presentationId: toNumericId(presentationId),
    slideId: toNumericId(hostAhaSlideId),
  })
}

// AHA-46329 desktop insert: bakes each clone's binding into the donor BEFORE exporting it.
//
// Why this exists. Every frame — host and clones alike — used to resolve "which AhaSlides
// slide am I bound to?" by asking which PowerPoint slide it lives on, via
// getSelectedDataAsync. That call is DOCUMENT-GLOBAL: it returns the user's current
// selection, the same answer in every frame. Seven clones booting at staggered times
// therefore each sampled a moving selection, which is what produced the duplicated and
// missing slides ("slide 6 before slide 3, three copies of slide 3"). No amount of timing
// or load-speed work fixes that — at zero latency all seven frames simply agree on the
// same wrong answer.
//
// On desktop a cloned slide inherits a SNAPSHOT of the donor's document.settings as of the
// moment exportAsBase64 ran. So if settings hold slideIds[i] at export time, the clone comes
// up already holding slideIds[i] — per-clone identity, carried in the clone itself, with no
// selection lookup anywhere on the load path. The web cannot use this: settings are shared
// live across frames there, so it keeps the original single-export path.
//
// Cost: one export plus one settings round-trip PER clone, where the web path pays a single
// shared export. There is no cheaper way to vary the payload — the whole point is that each
// clone must carry a DIFFERENT snapshot, and a snapshot is only taken at export.
//
// Tags are still written, kept in step with the baked settings so the two can never
// disagree. The desktop LOAD path no longer reads them (see openSlideFromHostSlideTag).
const runDesktopInsert = async ({
  presentationId,
  slideIds,
  hostSlideIndex,
  insertedSlideIds,
  onHostResolved,
}) => {
  const { hostSlideId, slideIdList } = await readHostSlideState(hostSlideIndex)
  // Reported as soon as it is known so the caller's rollback can strip the host's tags even
  // if this throws part-way through.
  onHostResolved(hostSlideId)

  let knownSlideIds = slideIdList
  let targetSlideId = hostSlideId

  for (let i = 1; i < slideIds.length; i += 1) {
    // Persisted BEFORE the export, so this clone's snapshot carries this clone's binding.
    // eslint-disable-next-line no-await-in-loop
    await saveDocumentSetting(DATA_SLIDE_SETTING_KEY, {
      presentationId: toNumericId(presentationId),
      slideId: toNumericId(slideIds[i]),
    })

    // Snapshotted into per-iteration consts: the batch closure below must not capture the
    // mutable running values (no-loop-func), same reason the web path snapshots `before`.
    const before = knownSlideIds
    const target = targetSlideId
    let insertedId = null
    let after = null

    // Sequential by design, exactly as on the web path: each insert targets the slide the
    // previous iteration created, so its id must be resolved before the next insert.
    // eslint-disable-next-line no-await-in-loop
    await window.PowerPoint.run(async (context) => {
      const { presentation } = context
      const { slides } = presentation

      // Re-exported every iteration — that is the mechanism, not an oversight: each export
      // captures the settings snapshot written immediately above.
      const donor = slides.getItemAt(hostSlideIndex - 1).exportAsBase64()
      await context.sync()

      presentation.insertSlidesFromBase64(donor.value, { targetSlideId: target })
      await context.sync()
      after = await loadSlideIds(context, slides)

      insertedId = resolveInsertedId(after, before, target)
      if (!insertedId) {
        throw new Error('Could not identify the inserted PowerPoint slide')
      }
      insertedSlideIds.push(insertedId)

      tagSlide(slides.getItem(insertedId), presentationId, slideIds[i])
      await context.sync()
    })

    knownSlideIds = after
    targetSlideId = insertedId
    log(`desktop clone ${i} inserted with baked slideId ${slideIds[i]}`)
  }

  await window.PowerPoint.run(async (context) => {
    tagSlide(context.presentation.slides.getItem(hostSlideId), presentationId, slideIds[0])
    await context.sync()
  })

  // Settings currently hold the last clone's binding; hand them back to the host frame.
  await restoreHostBinding(presentationId, slideIds[0])
}

// The original insert: ONE donor export, reused for every clone, all inside a single batch.
//
// Every clone therefore carries the same document.settings snapshot, which on the web is
// moot anyway — settings are shared live across frames there, so nothing in them can tell
// the clones apart. The per-slide tags written below are the only binding a web clone can
// read, and reading them still goes through the document-global getSelectedDataAsync. This
// path remains exposed to that defect; the desktop path above is the fix, and the web needs
// per-clone URL identity to get the same guarantee.
//
// Signature deliberately mirrors runDesktopInsert so the caller can dispatch between them
// without knowing anything about either.
const runWebInsert = ({
  presentationId,
  slideIds,
  hostSlideIndex,
  insertedSlideIds,
  onHostResolved,
}) => {
  return window.PowerPoint.run(async (context) => {
    const { presentation } = context
    const { slides } = presentation

    const hostSlide = slides.getItemAt(hostSlideIndex - 1)
    hostSlide.load('id')
    const donor = hostSlide.exportAsBase64()
    // Piggy-backed on the sync we already have to pay for: this is the "before" list the
    // first iteration diffs against, so the loop never needs a load of its own up front.
    slides.load('items/id')
    await context.sync()

    const hostSlideId = hostSlide.id
    onHostResolved(hostSlideId)
    const donorBase64 = donor.value
    // Running id list, carried across iterations: each iteration's "after" becomes the
    // next iteration's "before", so we load the collection once per insert instead of twice.
    let knownSlideIds = slides.items.map((slide) => slide.id)

    // One insert per extra selection, each targeting the previously inserted slide, so
    // the clones land in selection order directly after the add-in's own slide.
    let targetSlideId = hostSlideId
    for (let i = 1; i < slideIds.length; i += 1) {
      // Sequential by design: each insert targets the slide the PREVIOUS iteration created,
      // so its id has to be resolved before the next insert can be issued.
      presentation.insertSlidesFromBase64(donorBase64, { targetSlideId })
      // eslint-disable-next-line no-await-in-loop
      await context.sync()
      // eslint-disable-next-line no-await-in-loop
      const after = await loadSlideIds(context, slides)

      // Snapshot into a per-iteration const: the diff closure must not capture the mutable
      // running list (no-loop-func).
      const before = knownSlideIds
      const insertedId = resolveInsertedId(after, before, targetSlideId)
      if (!insertedId) {
        throw new Error('Could not identify the inserted PowerPoint slide')
      }
      insertedSlideIds.push(insertedId)
      knownSlideIds = after
      targetSlideId = insertedId

      // Tag the clone HERE, not in a batch after the loop. On Office web,
      // insertSlidesFromBase64 materialises the slide as soon as its sync lands and
      // PowerPoint activates the content add-in on it immediately — that frame then
      // resolves its binding from its host slide's tags exactly once, at load. Tagging
      // every clone only at the end of the batch left clone #1 untagged for N-1 further
      // sync round-trips, so it could load, find no tag, fall through to the shared
      // document.settings (still holding the pre-Add binding), and render the WRONG
      // AhaSlides slide — permanently, since the binding is never re-read. Queuing the
      // tag now means it rides the very next context.sync() instead.
      tagSlide(slides.getItem(insertedId), presentationId, slideIds[i])
    }

    // The host slide is the frame doing the inserting: it is already bound (its own
    // document.settings binding is written by the caller) and cannot race a clone, so its
    // tag can ride the final sync. Until this lands the clones are unbound copies of the
    // donor, so a failure anywhere in this batch must still roll back.
    tagSlide(slides.getItem(hostSlideId), presentationId, slideIds[0])
    await context.sync()
  })
}

// Clones the add-in's own slide once per extra selection.
// slideIds are AhaSlides slide ids in presentation order: slideIds[0] binds to the slide
// the add-in is already on; each of the rest gets a new PowerPoint slide, inserted in
// order immediately after it.
export const insertSlidesForSelection = async ({ presentationId, slideIds }) => {
  if (!isBulkSlideAddSupported()) {
    throw new Error('Bulk slide add is not supported on this version of PowerPoint')
  }
  if (!slideIds || !slideIds.length) {
    throw new Error('No slides selected')
  }

  const hostSlideIndex = await readHostSlideIndex('insert')
  const insertedSlideIds = []
  let hostSlideId = null

  Sentry.addBreadcrumb({
    category: 'ppt-office-api',
    message: `insertSlidesForSelection: ${slideIds.length} slide(s)`,
    level: 'info',
  })

  // Clean the donor before exporting it, so clones can't inherit a previous Add's binding.
  await stripHostSlideTags(hostSlideIndex)

  // Desktop bakes a per-clone settings snapshot; the web reuses one shared export. The two
  // share a signature, so dispatching is a lookup rather than a branch through this function.
  const desktop = isDesktopOfficeHost()
  log('insert path =', desktop ? 'desktop (per-clone settings snapshot)' : 'web (shared export)')

  const run = (desktop ? runDesktopInsert : runWebInsert)({
    presentationId,
    slideIds,
    hostSlideIndex,
    insertedSlideIds,
    onHostResolved: (id) => { hostSlideId = id },
  })

  try {
    await withTimeout(run, insertTimeoutMs(slideIds.length), 'Timed out while inserting PowerPoint slides')
  } catch (error) {
    log('insert failed', error)
    Sentry.captureException(error)
    // withTimeout can only stop WAITING for the batch — Office.js has no cancellation, so
    // on a timeout the run may still be inserting slides. Let it settle before rolling
    // back, otherwise this snapshot of insertedSlideIds is stale and we orphan whatever
    // lands after it. That settle-wait is itself bounded by SETTLE_GRACE_MS: a frozen or
    // crashed host may never settle `run` at all, and this function now backs a UI spinner,
    // so an unbounded wait here would hang the caller forever with no way to show an error.
    // Roll back with whatever insertedSlideIds holds once the grace period elapses either
    // way — trading a small residual orphan-slide risk in the frozen-host case (acceptable:
    // if the host is truly frozen, nothing further is landing anyway) for a caller that
    // always gets a bounded, actionable failure. Either way the ORIGINAL error is what we
    // throw below, never a rollback error or a grace-timeout error.
    await waitForSettleWithGrace(run, SETTLE_GRACE_MS)
    await rollbackInsertedSlides(insertedSlideIds, hostSlideId)
    if (desktop) {
      // The desktop loop leaves document.settings holding whichever clone it was baking when
      // it failed. Those settings belong to the HOST frame: left pointing at a clone's slide,
      // the host would come back bound to a slide the user was told had failed to add.
      // Best-effort — a restore failure must not mask the original error.
      try {
        await restoreHostBinding(presentationId, slideIds[0])
      } catch (restoreError) {
        log('restore host binding after failed insert failed', restoreError)
        Sentry.captureException(restoreError)
      }
    }
    throw error
  }

  log('inserted', insertedSlideIds, 'host =', hostSlideId)
  return { hostSlideId, insertedSlideIds }
}

// Removes this frame's binding from its own host slide's tags.
//
// The slide tag is the HIGHEST-precedence binding source on load (it has to be — it is the
// only thing that can tell cloned frames apart). That cuts both ways: when the user
// deliberately LEAVES a presentation, clearing document.settings and the last-selected
// presentation tag is not enough, because the slide tag outranks both. The editor's back
// path does a full page reload, so the next load would read the leftover tag and route
// straight back into the presentation the user just left — trapping them in it with no way
// out. Anything that unbinds a frame must therefore clear this tag too.
//
// Best-effort and never throws: failing to clear a tag must not block navigation.
export const clearSlideBindingFromHostSlide = async () => {
  if (!canReadSlideTags()) {
    return
  }
  try {
    const hostSlideIndex = await readHostSlideIndex('clear')
    await window.PowerPoint.run(async (context) => {
      clearTagsOnSlide(context.presentation.slides.getItemAt(hostSlideIndex - 1))
      await context.sync()
    })
    log('cleared host slide binding')
  } catch (error) {
    log('clear host slide binding failed', error)
    Sentry.captureException(error)
  }
}

// Mirrors a frame's document.settings binding onto its own host PowerPoint slide's tags, so
// the two never diverge. Called centrally from saveDataToSlidePowerPoint (office/index.js)
// on EVERY dataSlidePPT write.
//
// Why this has to exist: the slide tag is the highest-precedence binding source on load and
// the ONLY one a cloned frame can read. Before this, only the selector's insert path wrote
// it, so a later rebind through the ordinary editor buttons (which write settings only) was
// silently reverted by the stale tag on the next load, and clearing settings on "leave the
// editor" left the tag behind to trap the user in the presentation they'd left. Keeping the
// tag in step with every settings write closes both.
//
// A single-slide binding ({presentationId, slideId}) writes both tags; a whole-presentation
// binding ({activePresentationId}, no single slide) or a clear (null) removes them, so the
// tag never outranks settings with a slide the binding no longer points at. Resolves the
// target via getSelectedDataAsync — reliable for the editor writes that call this, where the
// frame's own slide is the selected one. Best-effort, never throws.
export const writeSlideBindingToHostSlide = async (value) => {
  if (!canReadSlideTags()) {
    return
  }
  const presentationId = value && value.presentationId
  const slideId = value && value.slideId
  try {
    const hostSlideIndex = await readHostSlideIndex('mirror')
    await window.PowerPoint.run(async (context) => {
      const slide = context.presentation.slides.getItemAt(hostSlideIndex - 1)
      if (presentationId && slideId) {
        tagSlide(slide, presentationId, slideId)
      } else {
        clearTagsOnSlide(slide)
      }
      await context.sync()
    })
    log('mirrored binding to host slide', presentationId, slideId)
  } catch (error) {
    log('mirror binding to host slide failed', error)
    Sentry.captureException(error)
  }
}

// Reads this frame's binding off its own host slide's tags. Returns null — never throws —
// so the load path can fall through to the legacy document.settings binding.
export const readSlideBindingFromHostSlide = async () => {
  if (!canReadSlideTags()) {
    return null
  }
  try {
    const hostSlideIndex = await readHostSlideIndex('read')
    let binding = null

    await window.PowerPoint.run(async (context) => {
      const slide = context.presentation.slides.getItemAt(hostSlideIndex - 1)
      const presentationTag = slide.tags.getItemOrNullObject(SLIDE_TAG_PRESENTATION_KEY)
      const slideTag = slide.tags.getItemOrNullObject(SLIDE_TAG_SLIDE_KEY)
      presentationTag.load('value,isNullObject')
      slideTag.load('value,isNullObject')
      await context.sync()

      if (presentationTag.isNullObject || slideTag.isNullObject) {
        return
      }
      binding = { presentationId: presentationTag.value, slideId: slideTag.value }
    })

    log('host slide binding =', binding)
    return binding
  } catch (error) {
    log('read host slide binding failed', error)
    return null
  }
}
